import React, { ChangeEvent, DragEvent, useRef, useState } from 'react';
import classNames from 'classnames';

import { validFileTypes } from '~constants/files';

import Button from '~components/Button';
import { validateFile } from '~components/file-area/FileValidation';
import Icon, { faCloudUploadAlt, faFileAlt } from '~components/icon';

import { InvoiceFile } from '~support-at-home/models';

interface ComponentProps {
  files: InvoiceFile[];
  onChange: (file: any) => void;
  setFilePreview: (filePreview: string) => void;
  setFile: (file: any) => void;
  setCurrentIndex: (index: number) => void;
  setError: (error: string | null) => void;
  isExisting?: boolean;
}

export const FileUpload: React.FC<ComponentProps> = ({
  files,
  onChange,
  setFilePreview,
  setFile,
  setCurrentIndex,
  setError,
  isExisting,
}) => {
  const [isOver, setIsOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleDragOver = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsOver(true);
  };

  const handleDragLeave = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsOver(false);
  };

  const handleFileChange = ({ target }: ChangeEvent<HTMLInputElement>) => {
    const [selectedFile] = target.files || [];
    if (selectedFile) {
      const valid = handleFileValidation(selectedFile);
      if (valid) {
        onChange(selectedFile);
        setCurrentIndex(files.length);
      }
    }
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsOver(false);
    const [droppedFile] = event.dataTransfer.files;
    if (droppedFile) {
      const valid = handleFileValidation(droppedFile);
      if (valid) {
        onChange(droppedFile);
        setCurrentIndex(files.length);
      }
    }
  };

  const handleFileValidation = (file: File) => {
    const validationResult = validateFile(file);

    if (validationResult !== true) {
      setError(validationResult as string);
      return false;
    }

    setError(null);
    setFile(file);

    const reader = new FileReader();
    reader.onloadend = ({ target }) => {
      if (!isExisting) setFilePreview(target?.result as string);
    };
    reader.onerror = () => {
      setError('Failed to read file');
    };
    reader.readAsDataURL(file);
    return true;
  };

  const classname = classNames('file-area upload', { isOver });
  const acceptTypes = validFileTypes.map((fileType) => fileType.mimeType).join(', ');

  return (
    <div className={classname} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}>
      <input
        type="file"
        id="file"
        accept={acceptTypes}
        ref={fileInputRef}
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />
      <label htmlFor="file" className="label">
        <Icon icon={faCloudUploadAlt} /> <strong>Select or drag a file</strong>
        <span className="dragndrop"> or drag it here</span>.
      </label>
      <Button className="button" type="button" onClick={() => fileInputRef.current?.click()}>
        <Icon icon={faFileAlt} /> Upload File
      </Button>
    </div>
  );
};
